import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { MapPin, Search, Calendar } from 'lucide-react';
import { locations } from '../mockData';

const SearchBar = ({ variant = 'hero' }) => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [location, setLocation] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);

  const filteredLocations = locations.filter(loc =>
    loc.toLowerCase().includes(location.toLowerCase())
  );

  const handleSearch = () => {
    if (location) {
      navigate(`/search?location=${encodeURIComponent(location)}`);
    } else {
      navigate('/search');
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  if (variant === 'compact') {
    return (
      <div className="flex gap-2">
        <div className="relative flex-1">
          <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
          <Input
            type="text"
            placeholder={t('searchPlaceholder')}
            value={location}
            onChange={(e) => {
              setLocation(e.target.value);
              setShowSuggestions(true);
            }}
            onKeyPress={handleKeyPress}
            onFocus={() => setShowSuggestions(true)}
            onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
            className="pl-10"
          />
          {showSuggestions && filteredLocations.length > 0 && (
            <div className="absolute z-10 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-60 overflow-auto">
              {filteredLocations.map((loc) => (
                <div
                  key={loc}
                  className="px-4 py-2 hover:bg-gray-50 cursor-pointer"
                  onClick={() => {
                    setLocation(loc);
                    setShowSuggestions(false);
                  }}
                >
                  {loc}
                </div>
              ))}
            </div>
          )}
        </div>
        <Button 
          onClick={handleSearch}
          className="bg-[#bcef3c] hover:bg-[#a8d92e] text-gray-900 font-medium"
        >
          <Search className="w-4 h-4 mr-2" />
          {t('searchButton')}
        </Button>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl shadow-xl p-6 max-w-4xl mx-auto">
      <div className="flex flex-col md:flex-row gap-4">
        {/* Location Input */}
        <div className="relative flex-1">
          <MapPin className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
          <Input
            type="text"
            placeholder={t('searchPlaceholder')}
            value={location}
            onChange={(e) => {
              setLocation(e.target.value);
              setShowSuggestions(true);
            }}
            onKeyPress={handleKeyPress}
            onFocus={() => setShowSuggestions(true)}
            onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
            className="pl-12 h-14 text-base"
          />
          {showSuggestions && filteredLocations.length > 0 && (
            <div className="absolute z-10 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-60 overflow-auto">
              {filteredLocations.map((loc) => (
                <div
                  key={loc}
                  className="px-4 py-3 hover:bg-gray-50 cursor-pointer flex items-center"
                  onClick={() => {
                    setLocation(loc);
                    setShowSuggestions(false);
                  }}
                >
                  <MapPin className="w-4 h-4 mr-2 text-gray-400" />
                  {loc}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Search Button */}
        <Button 
          onClick={handleSearch}
          className="bg-[#bcef3c] hover:bg-[#a8d92e] text-gray-900 font-semibold h-14 px-8 text-base"
        >
          <Search className="w-5 h-5 mr-2" />
          {t('searchButton')}
        </Button>
      </div>
    </div>
  );
};

export default SearchBar;